import React from 'react'
import Icon from '../../../ui/Icon/Icon'
import {
  BLOG_GALLERY_LAYOUT,
  normalizeBlogGalleryLayout,
  safeBlogHref,
} from '../../../api/blog/blogConstants'
import { isTextBlock } from '../editor/blockFactory'
import { tiptapJsonToHtml } from './tiptapJsonToHtml'
import usePairedImageAspect from './usePairedImageAspect'
import BlogProductSetBlock from './BlogProductSetBlock'
import styles from './BlogArticle.module.scss'

export function BlogArticleImage({ block, allowNavigation = false }) {
  const image = <img src={block.photo.imageUrl} alt={block.alt || ''} />
  const href = safeBlogHref(block.href)
  return href ? (
    <a
      className={styles.imageLink}
      href={href}
      target={block.openInNewTab ? '_blank' : undefined}
      rel="noopener noreferrer"
      onClick={(event) => {
        if (!allowNavigation) event.preventDefault()
      }}
    >
      {image}
    </a>
  ) : image
}

function TextHtml({ content, className }) {
  const html = tiptapJsonToHtml(content)
  if (!html || html === '<p></p>') return null
  return <div className={className || styles.prose} dangerouslySetInnerHTML={{ __html: html }} />
}

function BeforeAfterBlock({ block }) {
  const beforeUrl = block.before?.photo?.imageUrl || null
  const afterUrl = block.after?.photo?.imageUrl || null
  const aspectRatio = usePairedImageAspect(beforeUrl, afterUrl)
  if (!beforeUrl && !afterUrl) return null
  const imgStyle = aspectRatio ? { aspectRatio } : undefined

  return (
    <div className={styles.beforeAfter}>
      <div>
        <p className={styles.beforeAfterLabel}>До</p>
        {beforeUrl ? <img src={beforeUrl} alt={block.before?.alt || 'До'} style={imgStyle} /> : null}
      </div>
      <div>
        <p className={styles.beforeAfterLabel}>Після</p>
        {afterUrl ? <img src={afterUrl} alt={block.after?.alt || 'Після'} style={imgStyle} /> : null}
      </div>
    </div>
  )
}

function GalleryBlock({ block }) {
  const items = Array.isArray(block.items) ? block.items.filter((item) => item?.photo?.imageUrl) : []
  if (!items.length) return null
  const layout = normalizeBlogGalleryLayout(block.layout)

  if (layout === BLOG_GALLERY_LAYOUT.carousel) {
    const first = items[0]
    return (
      <div className={styles.galleryCarousel}>
        <img src={first.photo.imageUrl} alt={first.alt || ''} />
        {items.length > 1 ? (
          <p className={styles.galleryMeta}>
            1 / {items.length}
            {first.caption ? ` · ${first.caption}` : ''}
          </p>
        ) : first.caption ? (
          <p className={styles.galleryMeta}>{first.caption}</p>
        ) : null}
      </div>
    )
  }

  if (layout === BLOG_GALLERY_LAYOUT.collage) {
    const count = Math.min(items.length, 6)
    const collageClass = styles[`galleryCollage_${count}`] || ''
    return (
      <div className={`${styles.galleryCollage} ${collageClass}`.trim()}>
        {items.slice(0, 6).map((item, index) => (
          <img key={`${item.photo.imageUrl}-${index}`} src={item.photo.imageUrl} alt={item.alt || ''} />
        ))}
      </div>
    )
  }

  return (
    <div className={styles.galleryGrid}>
      {items.map((item, index) => (
        <img key={`${item.photo.imageUrl}-${index}`} src={item.photo.imageUrl} alt={item.alt || ''} />
      ))}
    </div>
  )
}

function BlogArticleBlock({ block, allowNavigation = false }) {
  if (!block) return null

  if (isTextBlock(block)) {
    return <TextHtml content={block.content} />
  }

  if (block.type === 'image') {
    if (!block.photo?.imageUrl) return null
    return (
      <figure className={styles.figure}>
        <BlogArticleImage block={block} allowNavigation={allowNavigation} />
        {block.caption ? <figcaption>{block.caption}</figcaption> : null}
      </figure>
    )
  }

  if (block.type === 'gallery') {
    return <GalleryBlock block={block} />
  }

  if (block.type === 'beforeAfter') {
    return <BeforeAfterBlock block={block} />
  }

  if (block.type === 'imageText') {
    const hasPhoto = Boolean(block.photo?.imageUrl)
    const text = <TextHtml content={block.content} />
    if (!hasPhoto && !text) return null
    return (
      <div
        className={`${styles.imageText} ${block.imagePosition === 'right' ? styles.imageTextRight : ''}`.trim()}
      >
        {hasPhoto ? <BlogArticleImage block={block} allowNavigation={allowNavigation} /> : <div />}
        <div>{text}</div>
      </div>
    )
  }

  if (block.type === 'youtube') {
    if (!block.videoId) return null
    return (
      <div className={styles.youtube}>
        <iframe
          title="YouTube"
          src={`https://www.youtube.com/embed/${encodeURIComponent(block.videoId)}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    )
  }

  if (block.type === 'videoUpload') {
    if (!block.video?.url) return null
    return (
      <div className={styles.video}>
        <video
          src={block.video.url}
          poster={block.poster?.imageUrl || undefined}
          controls
          playsInline
        />
        {block.caption ? <p className={styles.galleryMeta}>{block.caption}</p> : null}
      </div>
    )
  }

  if (block.type === 'productSet') {
    const refs = Array.isArray(block.productRefs) ? block.productRefs : []
    if (!refs.length && !block.title) return null
    return <BlogProductSetBlock block={block} />
  }

  if (block.type === 'ameButton') {
    if (!block.label && !block.href) return null
    const href = block.href || '#'
    return (
      <div className={styles.buttonWrap}>
        <a
          className={styles.button}
          href={href}
          target={block.openInNewTab ? '_blank' : undefined}
          rel={block.openInNewTab ? 'noopener noreferrer' : undefined}
          onClick={(event) => {
            if (!allowNavigation) event.preventDefault()
          }}
        >
          <Icon name="long-arrow-right" weight="light" />
          {block.label || 'Кнопка'}
        </a>
      </div>
    )
  }

  return null
}

export default BlogArticleBlock
